import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, noop } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { VisitorModel } from 'client/data/models/visitor';
import { DealerInfoModel, getDealerInfoPath, DealerInfoEntities } from 'client/data/models/dealer-info';
import { PATHS as PrivateListingPaths, PrivateListingModel } from 'client/data/models/private-listing';
import { InventoryEntities } from 'client/data/models/inventory';

import { isPrivateParty, isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { BOL_VDP_DEALERS } from 'site-modules/shared/constants/inventory/buy-online-dealers';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { createNicePhone } from 'site-modules/shared/utils/phone-format';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isVehicleMakePorsche } from 'site-modules/shared/utils/inventory/make';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

import { ContactDealerLayout } from './contact-dealer-layout';

/**
 * @typedef {Object} ContactDealerContextValue
 * @property {string} domainCase
 * @property {string} formattedPhone
 * @property {boolean} isEmbeddedCarcode
 * @property {Object} ctasOptions
 * @property {Object} vinData
 * @property {Object} dealerInfo
 * @property {Object} [dealer]
 * @property {Object} [privateListingData]
 * @property {boolean} isMobile
 * @property {boolean} isVlaPage
 * @property {string} pathname
 * @property {string} visitorZipCode
 * @property {string} visitorStateCode
 * @property {Object} featureFlags
 * @property {boolean} isNonTMVDealer
 * @property {boolean} isPrivatePartyListing
 * @property {boolean} showLeasePayments
 * @property {boolean} isTopEmbeddedLeadFormEnabled
 * @property {string|null} tcpaParentDealershipName
 * @property {boolean} isDealerCarcodeSuspended
 * @property {boolean} isVdpNonPartner
 * @property {boolean} isDealerWebsiteLinkHidden
 * @property {boolean} isCapOneAvailable
 * @property {boolean} isCapOneIneligible
 * @property {boolean} isCaramelOptInDealer
 * @property {boolean} isCarZingPrequalify
 * @property {boolean} hideLeadForm
 * @property {boolean} isProdEnv
 * @property {string} ted6504Recipe
 * @property {React.ReactNode} [buyOnlineBtn]
 * @property {Function} handleCaramelMakeOffer
 * @property {number} displayPrice
 * @property {number} [radius]
 * @property {string} [dataTrackingParent]
 * @property {boolean} isForceDefault
 * @property {boolean} isTED6504Enabled
 * @property {Object} location
 */

/** @type {React.Context<ContactDealerContextValue|null>} */
const ContactDealerContext = createContext(null);

/**
 * @returns {ContactDealerContextValue}
 */
export const useContactDealer = () => {
  return useContext(ContactDealerContext);
};

export const DOMAIN_CASES = {
  BOL_STANDARD: 'bol_standard',
  BOL_VLA: 'bol_vla',
  TRADITIONAL_NON_PARTNER: 'non_partner',
  PRIVATE_PARTY: 'private_party',
  TRADITIONAL_REGULAR: 'regular_partner',
};

export function ContactDealerUI({
  vinData,
  dealerInfo = {},
  isMobile = false,
  isVlaPage = false,
  pathname = '',
  featureFlags = {},
  showLeasePayments = false,
  radius,
  tcpaParentDealershipName = null,
  isTopEmbeddedLeadFormEnabled = false,
  isDealerCarcodeSuspended = false,
  location,
  dealer = null,
  visitorZipCode = '',
  visitorStateCode = '',
  isNonTMVDealer = false,
  isVdpNonPartner = false,
  isDealerWebsiteLinkHidden = false,
  isCapOneAvailable = false,
  isCapOneIneligible = false,
  isCaramelOptInDealer = false,
  isCarZingPrequalify = false,
  hideLeadForm = false,
  buyOnlineBtn = null,
  handleCaramelMakeOffer = noop,
  displayPrice = 0,
  isProdEnv = false,
  ted6504Recipe = 'ctrl',
  isForceDefault = false,
  isTED6504Enabled = false,
  shop5146Recipe = 'ctrl',
  ...restProps
}) {
  const isPrivatePartyListing = isPrivateParty(vinData);
  const parentDealershipName = get(vinData, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isBolDealer = BOL_VDP_DEALERS.includes(parentDealershipName);
  const isPartnerListing = isPartnerDealer(vinData);

  let domainCase = DOMAIN_CASES.TRADITIONAL_REGULAR;
  if (isPrivatePartyListing) {
    domainCase = DOMAIN_CASES.PRIVATE_PARTY;
  } else if (!isPartnerListing) {
    domainCase = DOMAIN_CASES.TRADITIONAL_NON_PARTNER;
  } else if (isBolDealer) {
    domainCase = isVlaPage ? DOMAIN_CASES.BOL_VLA : DOMAIN_CASES.BOL_STANDARD;
  }

  const isNewType = isNew(vinData.type);
  const isShop5146NciCopyChallenge = isNewType && shop5146Recipe === 'chal';

  const providerValue = {
    ...restProps,
    dealer,
    visitorZipCode,
    visitorStateCode,
    isNonTMVDealer,
    isVdpNonPartner,
    isDealerWebsiteLinkHidden,
    isCapOneAvailable,
    isCapOneIneligible,
    isCaramelOptInDealer,
    isCarZingPrequalify,
    hideLeadForm,
    buyOnlineBtn,
    handleCaramelMakeOffer,
    displayPrice,
    isProdEnv,
    ted6504Recipe,
    isForceDefault,
    isTED6504Enabled,
    vinData,
    dealerInfo,
    isMobile,
    isVlaPage,
    pathname,
    featureFlags,
    showLeasePayments,
    radius,
    tcpaParentDealershipName,
    isTopEmbeddedLeadFormEnabled,
    isDealerCarcodeSuspended,
    location,
    isPrivatePartyListing,
    domainCase,
    formattedPhone: createNicePhone(get(vinData, 'computedDisplayInfo.phoneNumber', '')),
    isEmbeddedCarcode: isTopEmbeddedLeadFormEnabled && !isDealerCarcodeSuspended && isPartnerListing && isMobile,
    ctasOptions: {
      featureFlags,
      showLeasePayments,
      pathname,
      dealerInfo,
      radius,
      isMobile,
      ...(location ? { location } : {}),
      tcpaParentDealershipName,
      isLeasePaymentEnabled:
        !!get(vinData, 'prices.tmv', 0) && !!get(vinData, 'prices.estimateLeasePromise.monthlyPayment', 0),
      ...(isNewType
        ? {
            titleText: isVehicleMakePorsche(vinData) ? 'Check Availability' : '',
            subtitleText: isVehicleMakePorsche(vinData) ? 'Enter your contact information' : '',
          }
        : {
            titleText: 'Check Availability',
            subtitleText: 'Please provide your contact info',
            submitButtonText: 'Request Information',
          }),
      ...(isShop5146NciCopyChallenge && {
        isShop5146NciCopyChallenge: true,
        titleText: 'Check Availability',
        subtitleText:
          'Enter your contact information to confirm this vehicle is still available before you head to the dealership!',
        submitButtonText: 'Request Information',
      }),
    },
  };

  return (
    <ContactDealerContext.Provider value={providerValue}>
      <ContactDealerLayout />
    </ContactDealerContext.Provider>
  );
}

ContactDealerUI.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  dealerInfo: DealerInfoEntities.DealerInfo,
  dealer: PropTypes.shape({}),
  privateListingData: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  isVlaPage: PropTypes.bool,
  pathname: PropTypes.string,
  visitorZipCode: PropTypes.string,
  visitorStateCode: PropTypes.string,
  featureFlags: PropTypes.shape({}),
  isNonTMVDealer: PropTypes.bool,
  showLeasePayments: PropTypes.bool,
  isTopEmbeddedLeadFormEnabled: PropTypes.bool,
  tcpaParentDealershipName: PropTypes.string,
  isDealerCarcodeSuspended: PropTypes.bool,
  location: PropTypes.shape({}),
  isVdpNonPartner: PropTypes.bool,
  isDealerWebsiteLinkHidden: PropTypes.bool,
  isCapOneAvailable: PropTypes.bool,
  isCapOneIneligible: PropTypes.bool,
  isCaramelOptInDealer: PropTypes.bool,
  isCarZingPrequalify: PropTypes.bool,
  hideLeadForm: PropTypes.bool,
  isProdEnv: PropTypes.bool,
  ted6504Recipe: PropTypes.string,
  buyOnlineBtn: PropTypes.node,
  handleCaramelMakeOffer: PropTypes.func,
  displayPrice: PropTypes.number,
  radius: PropTypes.number,
  dataTrackingParent: PropTypes.string,
  isForceDefault: PropTypes.bool,
  isTED6504Enabled: PropTypes.bool,
  shop5146Recipe: PropTypes.string,
};

export const mapStateToProps = state => ({
  featureFlags: state.featureFlags,
  shop5146Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'shop-5146-nci-copy',
    defaultVal: 'ctrl',
  }),
});

export const stateToPropsConfig = {
  visitorStateCode: bindToPath('location.stateCode', VisitorModel),
  pathname: bindToPath('location.pathname', PageModel),
  dealerInfo: bindToPath(({ vinData }) => getDealerInfoPath(vinData), DealerInfoModel),
  visitorZipCode: bindToPath('location.zipCode', VisitorModel),
  privateListingData: bindToPath(
    ({ vinData }) =>
      isPrivateParty(vinData) && vinData?.stockNumber
        ? PrivateListingPaths.LISTING_DATA({ listingId: vinData.stockNumber })
        : null,
    PrivateListingModel
  ),
};

export const ContactDealer = connect(mapStateToProps)(connectToModel(ContactDealerUI, stateToPropsConfig));
